/**
 * AsValueList Web Component
 *
 * Renders an ordered, editable list of primitive values. Supports float, int,
 * string, and bool element types. When linked to a Paramate array parameter via
 * trcad-param the element type is inferred automatically from the param data.
 *
 * Attributes:
 *   label-default-text   — optional label shown above the list
 *   element-type         — 'float' | 'int' | 'string' | 'bool'
 *                          Required when used without trcad-param.
 *                          Inferred from param metadata when trcad-param is set.
 *   value                — JSON array string for initial value without Paramate,
 *                          e.g. value="[0,1,2]"
 *   trcad-param          — Paramate parameter name (must be of type "array")
 *   configure-on-change  — trigger configure() immediately on every change
 *   debounce-ms          — Paramate send debounce in ms (default 150)
 *   no-debounce          — disables debounce
 *
 * Events dispatched:
 *   as-input-change — after any value or structural change
 *     detail: { type: 'array', value: any[], elementType: string | null, phase: 'change' }
 */

import './numbox.ts';
import './as-string-input.ts';
import './as-checkbox.ts';
import {
  registerParamateInput,
  unregisterParamateInput,
  markParamPending,
  clearParamPending,
  liveAppData,
  type ParamateLinkedInput,
} from '../../store/store.ts';
import styleText from './as-value-list.css?raw';

type ElementType = 'float' | 'int' | 'string' | 'bool';

function defaultForType(type: ElementType): any {
  if (type === 'string') return '';
  if (type === 'bool') return false;
  return 0;
}

export class AsValueList extends HTMLElement {
  static tag = 'as-value-list';
  static readonly DEFAULT_DEBOUNCE_MS = 150;
  private static _lightStyleAppended = false;

  private _paramName = '';
  private _elementType: ElementType | null = null;
  private _items: any[] = [];
  private _configurator: any = null;
  private _paramateEntry: ParamateLinkedInput | null = null;
  private _debounceMs = AsValueList.DEFAULT_DEBOUNCE_MS;
  private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _configureOnChange = false;
  private _hasRendered = false;
  private _listEl: HTMLElement | null = null;
  private _attrObserver: MutationObserver | null = null;
  private _placement: string | null = null;
  private _initialValueAttr: string | null = null;
  // Index of a newly-added row that is pending user confirmation (Enter/Escape)
  private _pendingRowIndex: number | null = null;
  private _lastConfirmedValue: any[] = [];

  private _areValuesEqual(a: any[], b: any[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
  }

  static get observedAttributes() {
    return ['label-default-text', 'element-type', 'placement'];
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback() {
    if (!this._hasRendered) {
      AsValueList._appendStyles();
      this._render();
      this._hasRendered = true;
    }

    this._initialValueAttr = this.getAttribute('value');

    this._configureOnChange = this.hasAttribute('configure-on-change');
    this._readDebounceAttrs();

    const elemTypeAttr = this.getAttribute('element-type') as ElementType | null;
    if (elemTypeAttr) this._elementType = elemTypeAttr;

    this._placement = this.getAttribute('placement');

    // Parse value attribute as JSON array for standalone use
    const valueAttr = this.getAttribute('value');
    if (valueAttr && this._items.length === 0) {
      try {
        const parsed = JSON.parse(valueAttr);
        if (Array.isArray(parsed)) this._items = parsed;
      } catch { /* ignore malformed JSON */ }
    }

    const paramName = this.getAttribute('trcad-param');
    if (paramName) {
      this._paramName = paramName;
      this._paramateEntry = registerParamateInput(paramName, this);
      this._requestConfigurator();
    }

    this.addEventListener(
      'as-paramate-value-resolved',
      this._handleParamateValueResolved as EventListener,
    );

    window.addEventListener('as-inputs-reset', this._handleProjectNew);
    window.addEventListener('as-restore-input-values', this._handleRestoreInputValues);
    window.addEventListener('as-restore-param-values', this._handleRestoreParamValues);

    this._attrObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        switch (m.attributeName) {
          case 'trcad-param':
            this._onParamAttributeChange(this.getAttribute('trcad-param'));
            break;
          case 'configure-on-change':
            this._configureOnChange = this.hasAttribute('configure-on-change');
            break;
          case 'debounce-ms':
          case 'no-debounce':
            this._readDebounceAttrs();
            break;
        }
      }
    });
    this._attrObserver.observe(this, {
      attributes: true,
      attributeFilter: ['trcad-param', 'configure-on-change', 'debounce-ms', 'no-debounce'],
    });

    this._renderList();
  }

  disconnectedCallback() {
    if (this._debounceTimer !== null) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }

    this._attrObserver?.disconnect();
    this._attrObserver = null;

    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateEntry = null;
    }

    this.removeEventListener(
      'as-paramate-value-resolved',
      this._handleParamateValueResolved as EventListener,
    );

    window.removeEventListener('as-inputs-reset', this._handleProjectNew);
    window.removeEventListener('as-restore-input-values', this._handleRestoreInputValues);
    window.removeEventListener('as-restore-param-values', this._handleRestoreParamValues);
  }

  attributeChangedCallback(name: string, _old: string | null, newValue: string | null) {
    switch (name) {
      case 'label-default-text': {
        const el = this.querySelector('.as-value-list__label-text');
        if (el) el.textContent = newValue ?? '';
        break;
      }
      case 'element-type':
        this._elementType = newValue as ElementType | null;
        this._renderList();
        break;
      case 'placement':
        this._placement = newValue;
        this._renderList();
        break;
    }
  }

  // ============================================================================
  // Styles
  // ============================================================================

  private static _appendStyles() {
    if (AsValueList._lightStyleAppended) return;
    const styleEl = document.createElement('style');
    styleEl.textContent = styleText;
    styleEl.setAttribute('data-component', 'as-value-list');
    document.head.appendChild(styleEl);
    AsValueList._lightStyleAppended = true;
  }

  // ============================================================================
  // Rendering
  // ============================================================================

  private _render() {
    this.innerHTML = `
      <div class="as-value-list">
        <div class="as-value-list__header">
          <span class="as-value-list__label-text">${this.getAttribute('label-default-text') ?? ''}</span>
        </div>
        <div class="as-value-list__items"></div>
        <button class="as-value-list__add-btn" type="button">+ Add item</button>
      </div>
    `;

    this._listEl = this.querySelector('.as-value-list__items');

    // Stop child input events from leaking into the global event stream.
    // We re-dispatch our own as-input-change with the full array instead.
    this._listEl?.addEventListener('as-input-update', (e: Event) => {
      e.stopPropagation();
    });

    this._listEl?.addEventListener('as-input-change', (e: Event) => {
      e.stopPropagation();
      const row = (e.target as HTMLElement).closest('[data-vl-index]') as HTMLElement | null;
      if (!row) return;
      const index = parseInt(row.dataset.vlIndex ?? '', 10);
      if (isNaN(index)) return;
      // Ignore changes from the pending (unconfirmed) row — wait for Enter
      if (index === this._pendingRowIndex) return;
      const value = AsValueList._readInputValue(e.target as HTMLElement);
      if (value !== undefined) {
        this._items[index] = value;
        this._emitChange();
        this._sendToParamate();
      }
    });

    this._listEl?.addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest(
        '.as-value-list__remove-btn',
      ) as HTMLElement | null;
      if (!btn) return;
      const row = btn.closest('[data-vl-index]') as HTMLElement | null;
      if (!row) return;
      const index = parseInt(row.dataset.vlIndex ?? '', 10);
      if (isNaN(index)) return;
      this._removeItem(index);
    });

    this.querySelector('.as-value-list__add-btn')?.addEventListener('click', () => {
      this._addItem();
    });
  }

  private _renderList() {
    if (!this._listEl) return;
    // A full re-render destroys any pending row, so clear the flag
    this._pendingRowIndex = null;
    this._listEl.innerHTML = '';
    this._items.forEach((val, i) => {
      this._listEl!.appendChild(this._createRow(i, val));
    });
  }

  private _createRow(index: number, value: any): HTMLElement {
    const row = document.createElement('div');
    row.className = 'as-value-list__row';
    row.dataset.vlIndex = String(index);

    row.appendChild(this._createInput(value));

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'as-value-list__remove-btn';
    removeBtn.textContent = '−';
    removeBtn.title = 'Remove';
    row.appendChild(removeBtn);

    return row;
  }

  private _createInput(value: any): HTMLElement {
    const type = this._elementType ?? 'float';
    let el: HTMLElement;

    switch (type) {
      case 'int': {
        el = document.createElement('as-numbox');
        el.setAttribute('value', String(value ?? 0));
        el.setAttribute('type', 'int');
        el.setAttribute('no-debounce', '');
        if (this._placement) el.setAttribute('placement', this._placement);
        break;
      }
      case 'string': {
        el = document.createElement('as-string-input');
        el.setAttribute('value', String(value ?? ''));
        el.setAttribute('no-debounce', '');
        break;
      }
      case 'bool': {
        el = document.createElement('as-checkbox');
        if (value) el.setAttribute('checked', '');
        break;
      }
      case 'float':
      default: {
        el = document.createElement('as-numbox');
        el.setAttribute('value', String(value ?? 0));
        el.setAttribute('no-debounce', '');
        if (this._placement) el.setAttribute('placement', this._placement);
        break;
      }
    }

    return el;
  }

  private static _readInputValue(el: HTMLElement): any {
    switch (el.tagName.toLowerCase()) {
      case 'as-numbox':
      case 'as-string-input':
        return (el as any).value;
      case 'as-checkbox':
        return (el as any).checked;
      default:
        return undefined;
    }
  }

  // ============================================================================
  // Item manipulation
  // ============================================================================

  private _addItem() {
    // Confirm any in-flight pending item before opening a new one
    if (this._pendingRowIndex !== null) {
      this._confirmPendingItem(this._pendingRowIndex);
    }

    const defaultVal = this._elementType ? defaultForType(this._elementType) : 0;
    this._items = [...this._items, defaultVal];
    const index = this._items.length - 1;
    this._pendingRowIndex = index;

    const row = this._createRow(index, defaultVal);
    row.classList.add('as-value-list__row--pending');
    this._listEl?.appendChild(row);

    // Intercept keydown in capture phase so we read the raw <input> value
    // before any child component's own Enter/Escape handler runs.
    row.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this._pendingRowIndex !== index) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        this._confirmPendingItem(index, row);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._cancelPendingItem(index);
      }
    }, { capture: true });

    // Focus and select the inner <input> after the child component has rendered
    requestAnimationFrame(() => {
      const inner = row.querySelector('input') as HTMLInputElement | null;
      if (inner) {
        inner.focus();
        inner.select?.();
      }
    });

    // Do NOT emit or send to Paramate — wait for confirmation
  }

  /**
   * Commit the pending row: read the current raw <input> value (which the child
   * component may not have committed yet), update _items, then emit and send.
   */
  private _confirmPendingItem(index: number, rowEl?: HTMLElement) {
    const row = rowEl
      ?? (this._listEl?.querySelector(`[data-vl-index="${index}"]`) as HTMLElement | null);

    if (row) {
      const inner = row.querySelector('input') as HTMLInputElement | null;
      if (inner) {
        const type = this._elementType ?? 'float';
        if (type === 'float') {
          const p = parseFloat(inner.value);
          this._items[index] = isNaN(p) ? 0 : p;
        } else if (type === 'int') {
          const p = parseFloat(inner.value);
          this._items[index] = isNaN(p) ? 0 : Math.round(p);
        } else if (type === 'string') {
          this._items[index] = inner.value;
        } else if (type === 'bool') {
          this._items[index] = inner.checked;
        }
      }
      row.classList.remove('as-value-list__row--pending');
    }

    this._pendingRowIndex = null;
    this._emitChange();
    this._sendToParamate();
  }

  /** Remove the pending (unconfirmed) row without sending to Paramate. */
  private _cancelPendingItem(index: number) {
    this._items.splice(index, 1);
    this._pendingRowIndex = null;
    this._renderList();
  }

  private _removeItem(index: number) {
    // If the user removes the pending row treat it as a cancel — no emit/send
    if (index === this._pendingRowIndex) {
      this._cancelPendingItem(index);
      return;
    }
    this._items = this._items.filter((_, i) => i !== index);
    this._renderList();
    this._emitChange();
    this._sendToParamate();
  }

  // ============================================================================
  // Events
  // ============================================================================

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('as-input-change', {
        bubbles: true,
        composed: true,
        detail: {
          type: 'array',
          value: [...this._items],
          elementType: this._elementType,
          phase: 'change',
        },
      }),
    );
  }

  // ============================================================================
  // Paramate integration
  // ============================================================================

  private _requestConfigurator() {
    if (!this._paramName) return;

    const onConfigurator = (event: CustomEvent<{ configurator: any }>) => {
      this._configurator = event.detail.configurator;
      this.removeEventListener('configurator', onConfigurator as EventListener);
      this._syncInitialValue();
    };

    this.addEventListener('configurator', onConfigurator as EventListener);
    window.dispatchEvent(new CustomEvent('get-configurator', { detail: { requester: this } }));
  }

  private _syncInitialValue() {
    if (!this._configurator?.params || !this._paramName) return;

    const fullName = this._paramName.startsWith('main:')
      ? this._paramName
      : `main:${this._paramName}`;

    const paramData = this._configurator.params[fullName];
    if (!paramData) return;

    if (paramData.elementType && !this._elementType) {
      this._elementType = paramData.elementType as ElementType;
    }

    const val = paramData.value ?? paramData.default;
    if (Array.isArray(val)) {
      this._items = [...val];
      this._renderList();
    }
  }

  private _handleParamateValueResolved = (
    event: CustomEvent<{ paramName: string; value: any }>,
  ) => {
    const { value } = event.detail;
    // undefined means an error occurred — keep current items
    if (value === undefined) return;

    // Opportunistically pick up elementType if we don't have it yet
    if (!this._elementType && this._configurator && this._paramName) {
      const fullName = this._paramName.startsWith('main:')
        ? this._paramName
        : `main:${this._paramName}`;
      const elementType = this._configurator.params?.[fullName]
        ?.elementType as ElementType | undefined;
      if (elementType) this._elementType = elementType;
    }

    if (Array.isArray(value)) {
      this._items = [...value];
      this._lastConfirmedValue = [...value];
      this._renderList();
    }
  };

  private _handleRestoreInputValues = (event: Event) => {
    const { inputs } = (event as CustomEvent<{ inputs: Record<string, any> }>).detail || {};
    if (!inputs || typeof inputs !== 'object') return;
    const id = this.id;
    if (!id || !(id in inputs)) return;
    const val = inputs[id];
    if (Array.isArray(val)) {
      this._items = [...val];
      this._renderList();
      if (this._paramName) {
        this._sendToParamate();
      }
    }
  };

  private _handleRestoreParamValues = (event: Event) => {
    if (!this._paramName) return;
    const { params } = (event as CustomEvent<{ params: Record<string, any> }>).detail || {};
    if (!params) return;

    const fullName = this._paramName.startsWith('main:') ? this._paramName : `main:${this._paramName}`;
    const value = fullName in params ? params[fullName]
      : this._paramName in params ? params[this._paramName]
      : undefined;
    if (value === undefined) return;

    if (Array.isArray(value)) {
      this._items = [...value];
      this._renderList();
      if (this.id) {
        liveAppData.uiInputs = { ...liveAppData.uiInputs, [this.id]: [...this._items] };
      }
      if (this._areValuesEqual(this._items, this._lastConfirmedValue)) {
        clearParamPending(this._paramName);
      } else {
        markParamPending(this._paramName, [...this._items]);
      }
    }
  };

  private _sendToParamate() {
    if (!this._configurator || !this._paramName) return;

    if (this._areValuesEqual(this._items, this._lastConfirmedValue)) {
      clearParamPending(this._paramName);
    } else {
      markParamPending(this._paramName, this._items);
    }

    if (this._debounceMs > 0) {
      if (this._debounceTimer !== null) clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => {
        this._debounceTimer = null;
        this._dispatchToParamate();
      }, this._debounceMs);
    } else {
      this._dispatchToParamate();
    }
  }

  private _dispatchToParamate() {
    if (!this._configurator || !this._paramName) return;

    const fullName = this._paramName.startsWith('main:')
      ? this._paramName
      : `main:${this._paramName}`;

    this.dispatchEvent(
      new CustomEvent('as-input-pending', {
        bubbles: true,
        composed: true,
        detail: { paramName: this._paramName, value: [...this._items] },
      }),
    );

    this._configurator.setParamValue?.(fullName, [...this._items], this._configureOnChange);
  }

  private _onParamAttributeChange(newValue: string | null) {
    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
      this._paramateEntry = null;
    }
    this._paramName = newValue ?? '';
    if (this._paramName) {
      this._paramateEntry = registerParamateInput(this._paramName, this);
      this._requestConfigurator();
    }
  }

  private _handleProjectNew = () => {
    const valueAttr = this._initialValueAttr;
    if (valueAttr) {
      try {
        const parsed = JSON.parse(valueAttr);
        if (Array.isArray(parsed)) {
          this._items = parsed;
          this._renderList();
          return;
        }
      } catch { /* skip */ }
    }
    this._items = [];
    this._renderList();
  };

  private _readDebounceAttrs() {
    if (this.hasAttribute('no-debounce')) {
      this._debounceMs = 0;
      return;
    }
    const attr = this.getAttribute('debounce-ms');
    if (attr !== null) {
      const parsed = parseInt(attr, 10);
      this._debounceMs =
        Number.isFinite(parsed) && parsed >= 0 ? parsed : AsValueList.DEFAULT_DEBOUNCE_MS;
    } else {
      this._debounceMs = AsValueList.DEFAULT_DEBOUNCE_MS;
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  get value(): any[] {
    return [...this._items];
  }

  set value(val: any[]) {
    if (!Array.isArray(val)) return;
    this._items = [...val];
    this._renderList();
  }
}

if (!customElements.get(AsValueList.tag)) {
  customElements.define(AsValueList.tag, AsValueList);
} else {
  console.warn('as-value-list already defined');
}
